import { type FunctionComponent, useState } from "react";
import ViewTable from "./ViewTable";
import ViewMap from "./ViewMap";
import { makeRipple } from "@/react/core/ripple-utils.ts";
import { router } from "@/pos/PosRouter.ts";
import { mainScreen } from "@/data/PosSettingsSignal.ts";
import PortalPopup from "@delivery-management/PortalPopup.tsx";
import DateChoosing from "@delivery-management/DeliveyMangement/Popup/DateChoosing.tsx";
import { filtersDelivery, filterType, setFilterType } from "@/react/DeliveryManagementView/DeliveryManagementLogic.tsx";
import dayjs from "dayjs";
import { LL2 } from "@/react/core/I18nBackend.tsx";
import { LL0 } from "@/react/core/I18nService.tsx";

export type DeliveryManagementPluginType = {
  className?: string;
};

const DeliveryManagementPlugin: FunctionComponent<
  DeliveryManagementPluginType
> = ({ className = "" }) => {

  const [viewMode, setViewMode] = useState<"table" | "map">("table")
  const [openFilter, setOpenFilter] = useState(false)
  const [isOpenDateFilterPopup, setIsOpenDateFilterPopup] = useState(false);

  return (
    <>
      {openFilter &&
        <div className="absolute top-0 right-0 bottom-0 left-0 z-[2]" onClick={() => setOpenFilter(false)} />}
      <div
        className={`w-full bg-[#374251] max-w-full h-full overflow-hidden flex flex-col items-start justify-start text-center text-[14px] text-[#000] font-[Mulish] ${className}`}
      >
        <div
          className="self-stretch rounded-[1px] bg-[#5a6370] flex flex-row items-start justify-start py-[8px] px-[12px] gap-[8px] z-[2] mq768:gap-[8px] mq768:pt-[6px] mq768:pb-[6px] mq768:box-border">
          <div className="flex-1 flex flex-row items-center justify-start gap-[8px]">
            <div
              className="rounded-[100px] bg-[#1271ff] h-[34px] overflow-hidden flex flex-row items-center justify-center py-[2px] px-[10px] box-border min-w-[72px] mq768:h-[30px]"
              ref={makeRipple}
              onClick={() => router.screen = mainScreen()}
            >
              <img
                className="w-[30px] relative h-[30px] object-cover"
                alt=""
                src="/iconback-icon@2x.png"
              />
            </div>
            <div className="flex flex-col items-start justify-start relative gap-[4px]">
              <div
                className="self-stretch rounded-[100px] bg-[#fff] h-[34px] overflow-hidden shrink-0 flex flex-row items-center justify-between py-[0px] px-[10px] box-border z-[0] mq768:h-[30px]"
                ref={makeRipple}
                onClick={() => setOpenFilter(v => !v)}
              >
                <div className="relative font-semibold sm:text-[13px] mq600:text-[12px] h.414:text-[12px]">
                  {filterType() === 'allOrders' ? LL0().deliveryManagement.allOrders() : LL0().deliveryManagement.preOrders()}
                </div>
                <div className="flex flex-row items-center justify-start mt-0.5">
                  {!openFilter ? (
                    <img
                      className="w-[24px] relative h-[24px] object-cover"
                      alt=""
                      src="/iconarrow-down@2x.png"
                    />
                  ) : (
                    <img
                      className="w-[24px] relative h-[24px] object-cover"
                      alt=""
                      src="/iconarrow-up@2x.png"
                    />
                  )}
                </div>
              </div>
              {openFilter && (
                <div className="w-full !m-[0] absolute top-[38px] right-[0px] left-[0px] shadow-[0px_4px_4px_rgba(0,_0,_0,_0.25)] rounded-[8px] bg-[#fff] overflow-y-auto flex flex-col items-start justify-start gap-[2px] z-[1]">
                  <div
                    className={`self-stretch overflow-hidden shrink-0 flex flex-row items-center justify-start p-[10px] relative ${filterType() === 'allOrders' ? 'bg-[#e2f0ff]' : ''}`}
                    onClick={() => {
                      setFilterType('allOrders');
                      setOpenFilter(false);
                    }}
                  >
                    <div className="relative font-medium z-[1] sm:text-[13px] mq600:text-[12px] h.414:text-[12px]">
                      {LL0().deliveryManagement.allOrders()}
                    </div>
                  </div>
                  <div
                    className={`self-stretch overflow-hidden shrink-0 flex flex-row items-center justify-start p-[10px] relative ${filterType() === 'preOrders' ? 'bg-[#e2f0ff]' : ''}`}
                    onClick={() => {
                      setFilterType('preOrders');
                      setOpenFilter(false);
                    }}
                  >
                    <div className="relative font-medium z-[1] sm:text-[13px] mq600:text-[12px] h.414:text-[12px]">
                      {LL0().deliveryManagement.preOrders()}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div
              className="flex flex-row items-center justify-center text-left text-[15px] text-[#fff]"
              ref={makeRipple}
              onClick={() => setIsOpenDateFilterPopup(true)}
            >
              <div
                className="relative [text-decoration:underline] font-semibold sm:text-[14px] mq600:text-[13px] h.414:text-[13px]">
                {!filtersDelivery()?.creationDate || filtersDelivery()?.creationDate === dayjs().startOf("day").unix() ? LL0().onlineOrder.today() + ", " : ""}
                {filtersDelivery()?.creationDate === dayjs().subtract(1, "day").startOf("day").unix() ? "Yesterday, " : ""}
                {dayjs.unix(filtersDelivery()?.creationDate || dayjs().unix()).format(LL2().dates.dateFormat())}
              </div>
            </div>
          </div>
          <div
            className="rounded-[9999px] bg-[#748193] h-[34px] flex flex-row items-center justify-start p-[2px] box-border text-[15px] text-[#fff] mq768:h-[30px]"
          >
            <div
              className="self-stretch rounded-[1000px] flex flex-row items-center justify-center py-[2px] px-[8px] box-border relative gap-[4px] min-w-[102px]"
              ref={makeRipple}
              onClick={() => setViewMode("table")}
            >
              {viewMode === "table" ?
                <div
                  className="w-full !m-[0] absolute h-full top-[0px] right-[0px] bottom-[0px] left-[0px] rounded-[1000px] border-[#5a6370] border-[1px] border-solid box-border flex flex-row items-center justify-center py-[4px] px-[10px] gap-[4px] z-[1] text-[#000] mq1440:gap-[4px] h.414:gap-[2px]">
                  <div
                    className="w-full absolute !m-[0] h-full top-[0%] right-[0%] bottom-[0%] left-[0%] rounded-[1000px] bg-[#fff] z-[0]" />
                  <div
                    className="relative font-semibold z-[1] sm:text-[14px] mq600:text-[13px] h.414:text-[13px] h.414:w-[74px]">
                    {LL0().deliveryManagement.viewTable()}
                  </div>
                </div>
                :
                <div className="flex-1 relative font-semibold z-[0] sm:text-[14px] mq600:text-[13px] h.414:text-[13px]">
                  {LL0().deliveryManagement.viewTable()}
                </div>}
            </div>
            <div
              className="self-stretch rounded-[1000px] flex flex-row items-center justify-center py-[2px] px-[8px] relative gap-[4px] min-w-[102px]"
              ref={makeRipple}
              onClick={() => setViewMode("map")}
            >
              {viewMode === "map" ?
                <div
                  className="w-full !m-[0] absolute h-full top-[0px] right-[0px] bottom-[0px] left-[0px] rounded-[1000px] border-[#5a6370] border-[1px] border-solid box-border flex-row items-center justify-center py-[4px] px-[10px] gap-[4px] z-[1] text-[#000] mq1440:gap-[4px] h.414:gap-[2px]">
                  <div
                    className="w-full absolute !m-[0] h-full top-[0%] right-[0%] bottom-[0%] left-[0%] rounded-[1000px] bg-[#fff] z-[0]" />
                  <div
                    className="relative font-semibold z-[1] sm:text-[14px] mq600:text-[13px] h.414:text-[13px] h.414:w-[74px]">
                    {LL0().deliveryManagement.viewMap()}
                  </div>
                </div>
                :
                <div className="relative font-semibold z-[0] sm:text-[14px] mq600:text-[13px] h.414:text-[13px]">
                  {LL0().deliveryManagement.viewMap()}
                </div>}
            </div>
          </div>
        </div>
        {/*{viewMode === "table" && <IsEmpty />}*/}
        {viewMode === "table" && <ViewTable />}
        {viewMode === "map" && <ViewMap type={'viewOrder'} />}
      </div>
      {isOpenDateFilterPopup &&
        <PortalPopup
          overlayColor="rgba(0, 0, 0, 0.2)"
          placement="Centered"
          onOutsideClick={() => setIsOpenDateFilterPopup(false)}
        >
          <DateChoosing onClose={() => setIsOpenDateFilterPopup(false)} />
        </PortalPopup>
      }
    </>
  );
};

export default DeliveryManagementPlugin;
